// Barra de navegación inferior de mobile —
// ADR-2026-07-31-home-mobile-hero-nav-sheet-generar, extendida a todo el shell por
// ADR-2026-08-04-la-barra-inferior-vive-en-el-shell.
//
// Cinco lugares: cuatro destinos y, en el medio, un botón de ACCIÓN que abre el
// sheet de "Generar" (no navega a ninguna parte). Ese botón es el centro de la
// pantalla: sale del círculo elevado del diseño, por encima del borde de la barra.
//
// Nació en la home y hoy la monta el shell (`ShellMobile`), así que se ve en TODAS
// las pantallas del rediseño y no sólo en el inicio. `MobileBottomNav`, la barra
// vieja, quedó para las pantallas que siguen bajo `DashboardLayout` a secas
// (`/modos` y el landing de Estudio).

import { NavLink } from 'react-router-dom';
import { useSidebar } from '../../../context/SidebarContext';
import { RUTA_CHAT_ATLAS } from '../../../chat-atlas/ui/rutaChatAtlas';
import { trackEvent } from '../../../lib/posthog';
import { CumbreMascot } from '../../../components/CumbreMascot';

type Tab = {
  to: string;
  label: string;
  /** Glifo de Material Symbols Rounded (la familia ya está cargada en `index.html`). */
  icono: string;
  /**
   * Dibuja a Cumbre —monocroma, del color del estado— en vez del glifo
   * (ADR-2026-09-22-la-mascota-es-el-icono-del-agente-en-la-barra). El `icono` se
   * conserva igual: es el de la sidebar de desktop y es a lo que se vuelve si esto se
   * saca.
   */
  mascota?: boolean;
  /** Se llama al tocar, ADEMÁS de navegar. Hoy sólo para medir. */
  onTap?: () => void;
};

/**
 * Lo que ocupa la barra, para que una pantalla pueda dejarle el lugar libre —
 * ADR-2026-08-04-el-modo-se-lleva-la-barra.
 *
 * Son los 64px de la fila (`h-16`) más el safe area de abajo, o sea lo mismo que la
 * barra reserva para sí. Se exporta como string de `calc` y no como número porque el
 * safe area sólo lo conoce el navegador; quien necesite el hueco lo mete en su
 * `padding-bottom` tal cual.
 */
export const ALTO_NAV_INFERIOR = 'calc(4rem + env(safe-area-inset-bottom, 0px))';

// Los cuatro destinos, en el orden del diseño. Son rutas que YA existen: la barra
// no inventa pantallas, sólo cambia cómo se llega.
//   · Plantillas y Creaciones son las mismas que enlaza la home de desktop.
//   · Marca es `/mi-marca`, el mismo destino —y el mismo glifo— que el tercer ítem de
//     la sidebar de desktop. Antes ahí estaba Perfil (`/configuracion`); la cuenta se
//     sigue tocando desde el menú del avatar, y lo que se edita seguido es la marca.
const TAB_INICIO: Tab = { to: '/interfaz-v2', label: 'Inicio', icono: 'home' };

/**
 * EL SEGUNDO LUGAR, QUE SIGUE AL RUBRO — ADR-2026-08-04-el-segundo-lugar-de-la-barra-sigue-al-rubro,
 * ampliado por ADR-2026-09-22-el-segundo-lugar-de-la-barra-es-el-agente.
 *
 * Tres casos, en este orden:
 *
 *  • **Plantillas**, para los rubros que la ven —Moda, y Belleza y Cuidado Personal,
 *    cada uno con SU pantalla (ADR-2026-09-10-plantillas-belleza-modo-propio).
 *  • **Agente**, para los rubros sin catálogo propio: son los mismos que abren la home
 *    CON el Agente (ADR-2026-09-20-la-home-de-los-otros-rubros-abre-con-el-agente), o
 *    sea aquellos para los que el Agente es la herramienta principal — y hasta acá era
 *    lo único importante sin lugar en la barra. En un teléfono, además, era lo único sin
 *    ninguna puerta permanente: el flotante "Preguntále a Atlas" es sólo de escritorio,
 *    así que saliendo de la home no había forma de volver al Agente.
 *  • **Catálogo**, para los que no son ni una cosa ni la otra —hoy, Hogar y Decoración—,
 *    donde además significa algo: el slug resuelve a SU catálogo, el de muebles
 *    (ADR-2026-08-20-el-slug-catalogo-no-tiene-card-en-hogar-y-deco).
 *
 * El lugar no se deja vacío ni se colapsa la barra a cuatro: son cinco lugares de
 * ancho fijo y sacar uno recentra los otros cuatro, con lo que el botón de Generar
 * deja de estar en el medio.
 *
 * Los nombres y los glifos son los de la sidebar de desktop (`Catálogo` / `grid_view`,
 * `Agente` / `forum`), como todo el resto de la barra
 * (ADR-2026-08-04-los-iconos-de-la-barra-son-los-de-la-sidebar).
 */
const tabPlantillas = (ruta: string): Tab => ({
  to: ruta,
  label: 'Plantillas',
  icono: 'dashboard_customize',
});
const TAB_CATALOGO: Tab = {
  to: '/interfaz-v2/modo/catalogo',
  label: 'Catálogo',
  icono: 'grid_view',
};
const TAB_AGENTE: Tab = {
  to: RUTA_CHAT_ATLAS,
  // "Asistente", como la sidebar (ADR-2026-10-16-el-agente-se-ve-como-el-asistente).
  label: 'Asistente',
  icono: 'forum',
  // El Agente es el único lugar con CARA: Cumbre en vez del glifo de la sidebar
  // (ADR-2026-09-22-la-mascota-es-el-icono-del-agente-en-la-barra). Monocroma y quieta,
  // así sigue el mismo estado de color que los otros cuatro.
  mascota: true,
  onTap: () => trackEvent('nav_agente_tap', { bloqueado: false }),
};
const TABS_DERECHA: Tab[] = [
  { to: '/redes-sociales/imagenes', label: 'Creaciones', icono: 'photo_library' },
  { to: '/mi-marca', label: 'Marca', icono: 'shopping_bag' },
];

/** Un destino: ícono + etiqueta, con el estado activo de la app (azul + relleno). */
function TabNav({
  tab,
  apagada = false,
  onSalir,
}: {
  tab: Tab;
  /** Hay una pantalla abierta encima (Herramientas): no se marca aunque su ruta sea la de
   *  atrás, así no quedan dos lugares en azul. */
  apagada?: boolean;
  /** Tocarla cierra lo que esté abierto encima (Herramientas), aunque no cambie la ruta. */
  onSalir?: () => void;
}) {
  return (
    <NavLink
      to={tab.to}
      end
      onClick={() => {
        onSalir?.();
        tab.onTap?.();
      }}
      className="flex flex-1 flex-col items-center justify-center gap-1 active:opacity-60"
    >
      {({ isActive }) => {
        // El estado lo marca el COLOR y nada más —azul de marca contra el gris—,
        // que es como lo marca la sidebar de desktop
        // (ADR-2026-08-04-los-iconos-de-la-barra-son-los-de-la-sidebar).
        //
        // El glifo se dibuja con los ajustes de la familia (`FILL 1, wght 400`), o
        // sea igual que allá: sin `.bottomnav-icon`, que los adelgazaba a `FILL 0,
        // wght 300`. Era la diferencia que quedaba: el mismo nombre de ícono, pero
        // de contorno fino en la barra y relleno en la sidebar.
        // El gris de los no seleccionados es el MISMO del shell de desktop
        // (`GRIS_SHELL`, negro al 60%) y no un `brand-black/45` propio. Además de
        // ser otro valor, `brand-black` no declara `<alpha-value>`: con modificador
        // de opacidad no emite regla y el color terminaba siendo el heredado.
        const color = isActive && !apagada ? 'text-brand-primary' : 'text-black/60';
        return (
          <>
            {tab.mascota ? (
              /* La mascota mide 24 de ancho como los glifos, pero es más baja que ancha
                 (294×263): va en una caja de 24 de alto, centrada, así la etiqueta de
                 abajo queda a la misma altura que en los otros cuatro lugares. */
              <span className={`flex h-6 items-center justify-center transition-colors ${color}`}>
                <CumbreMascot comoIcono decorativa size={24} />
              </span>
            ) : (
              <span
                className={`material-symbols-rounded text-[24px] leading-none transition-colors ${color}`}
              >
                {tab.icono}
              </span>
            )}
            <span className={`text-[11px] font-semibold leading-none transition-colors ${color}`}>
              {tab.label}
            </span>
          </>
        );
      }}
    </NavLink>
  );
}

/**
 * El Agente cuando el plan no lo incluye (Pro y Ultra —
 * ADR-2026-09-14-el-agente-es-de-pro-y-ultra).
 *
 * Se DIBUJA igual, con candado, y el toque va a planes: es la convención de la app —lo
 * bloqueado se ve y dice qué plan lo abre— y la misma que ya usan la card del Agente en
 * el sheet de Generar y los modos con candado. Esconderlo dejaría la barra con un lugar
 * distinto según el plan, y a estos rubros sin el atajo a lo único que les falta.
 *
 * Es un `button` y no un `NavLink`: no navega a `/agente` sino a planes, y no tiene
 * estado activo que marcar.
 */
function TabAgenteBloqueado({ onAbrir }: { onAbrir: () => void }) {
  return (
    <button
      type="button"
      onClick={() => {
        trackEvent('nav_agente_tap', { bloqueado: true });
        onAbrir();
      }}
      aria-label="Agente — disponible con los planes Pro y Ultra"
      className="flex flex-1 flex-col items-center justify-center gap-1 active:opacity-60"
    >
      {/* El candado va COLGADO del ícono y no al lado de la etiqueta: los cinco lugares
          son de ancho fijo y "Agente 🔒" en el renglón de la etiqueta obligaba a achicar
          el texto. El fondo blanco es lo que lo despega del glifo de abajo. */}
      <span className="relative leading-none text-black/60">
        <span className="flex h-6 items-center justify-center">
          <CumbreMascot comoIcono decorativa size={24} />
        </span>
        <span
          aria-hidden="true"
          className="material-symbols-rounded absolute -right-2 -top-0.5 rounded-full bg-white text-[14px] leading-none text-black/45"
        >
          lock
        </span>
      </span>
      <span className="text-[11px] font-semibold leading-none text-black/60">Agente</span>
    </button>
  );
}

/**
 * Un lugar de la barra que NO navega: abre algo sobre la pantalla en la que estás (hoy,
 * Herramientas en la interfaz nueva). Mismo dibujo que `TabNav`; `activa` mientras lo que
 * abrió está abierto, como un lugar más
 * (ADR-2026-10-09-herramientas-en-el-telefono-a-pantalla-completa-3).
 */
function TabAccion({
  label,
  icono,
  onClick,
  activa = false,
}: {
  label: string;
  icono: string;
  onClick: () => void;
  activa?: boolean;
}) {
  const color = activa ? 'text-brand-primary' : 'text-black/60';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={activa ? 'page' : undefined}
      className="flex flex-1 flex-col items-center justify-center gap-1 active:opacity-60"
    >
      <span className={`material-symbols-rounded text-[24px] leading-none transition-colors ${color}`}>{icono}</span>
      <span className={`text-[11px] font-semibold leading-none transition-colors ${color}`}>{label}</span>
    </button>
  );
}

export function NavInferiorMobile({
  onGenerar,
  rutaPlantillas,
  agente,
  uiNueva = false,
  onHerramientas,
  herramientasAbierta = false,
  onDejarHerramientas,
}: {
  onGenerar: () => void;
  /**
   * La interfaz nueva (grupo C) — ADR-2026-10-08-la-interfaz-nueva-llega-al-telefono. La
   * barra pasa a espejar la sidebar de escritorio: Inicio · Creaciones · Crear (abre los
   * destinos) · Herramientas (abre su lista) · Marca. Recrear y el Agente viven adentro
   * del sheet, como en escritorio viven en Redes sociales y en la sidebar.
   */
  uiNueva?: boolean;
  /** Abre el sheet en Herramientas. Sólo con `uiNueva`. */
  onHerramientas?: () => void;
  /** La pantalla de Herramientas está abierta: su lugar va en azul y los demás no. */
  herramientasAbierta?: boolean;
  /** Cierra Herramientas al tocar otro lugar de la barra. */
  onDejarHerramientas?: () => void;
  /**
   * La pantalla de Plantillas que ve la marca activa, o `null` si no ve ninguna.
   *
   * Es una RUTA y no un booleano porque hay dos modos de plantillas, uno por rubro
   * (ADR-2026-09-10-plantillas-belleza-modo-propio): con un `true` la barra no sabría
   * a cuál de las dos llevar. Entra por prop y no se resuelve acá: el rubro ya lo
   * tiene la home, que es quien monta esta barra, y con dos consultas distintas la
   * barra podría decir una cosa y la home otra. Al cambiar de marca la home se
   * re-renderiza y el lugar cambia solo.
   */
  rutaPlantillas: string | null;
  /**
   * El Agente en el segundo lugar, o `null` si a esta marca no le toca (ve Plantillas, o
   * tiene catálogo propio) — ADR-2026-09-22-el-segundo-lugar-de-la-barra-es-el-agente.
   *
   * Entra ya resuelto, como `rutaPlantillas` y por el mismo motivo: el rubro y el plan
   * los tiene el shell, y con dos consultas distintas la barra podría decir una cosa y la
   * home otra. `bloqueado` y `onAbrir` son los MISMOS que usa la card del Agente en el
   * sheet de Generar (`useModosGenerar`), así que el piso de plan se escribe una vez.
   */
  agente: { bloqueado?: boolean; onAbrir: () => void } | null;
}) {
  // "Modo tarea": la pantalla montó su propia barra de acción abajo (el Generar fijo
  // del paso Configurar, el CTA de selección de productos) o abrió un sub-flujo con
  // su propio back. La barra se desliza hacia abajo en vez de desmontarse de golpe,
  // igual que hacía `MobileBottomNav` — ADR-2026-08-04-el-modo-se-lleva-la-barra.
  const { mobileHideBottomNav } = useSidebar();
  /**
   * El segundo lugar, ya resuelto: Plantillas, Agente o Catálogo (ver la nota de arriba).
   * Es un NODO y no un `Tab` porque el Agente bloqueado no es un destino —no navega a
   * `/agente` sino a planes—, así que no entra en el `map` de `TabNav`.
   */
  const segundoLugar = rutaPlantillas ? (
    <TabNav tab={tabPlantillas(rutaPlantillas)} />
  ) : agente ? (
    agente.bloqueado ? (
      <TabAgenteBloqueado onAbrir={agente.onAbrir} />
    ) : (
      <TabNav tab={TAB_AGENTE} />
    )
  ) : (
    <TabNav tab={TAB_CATALOGO} />
  );
  return (
    <nav
      aria-hidden={mobileHideBottomNav}
      // Fondo blanco y opaco: la barra es una superficie, no un velo. Era
      // `#FAFAFA/95` —el gris del lienzo— y contra el fondo de la app, que desde
      // ADR-2026-08-04-mobile-usa-el-gris-de-desktop es ese mismo gris, no se
      // distinguía de la pantalla. El blanco la despega; el borde de arriba deja de
      // ser lo único que la separa del contenido.
      //
      // `md:hidden` y no `sm:hidden`: el shell mobile se monta por debajo de 1024px y
      // de 768 para arriba muestra la sidebar de escritorio, que es exactamente el
      // rango que cubría la barra vieja. Con `sm:` quedaba una franja de 640 a 768
      // sin ninguna navegación a la vista.
      // Con Herramientas abierta (z-70) la barra va por ENCIMA: la pantalla termina en el
      // borde de la barra, pero el círculo de Crear sobresale hacia arriba y quedaba cortado.
      className={`fixed inset-x-0 bottom-0 ${herramientasAbierta ? 'z-[71]' : 'z-40'} border-t border-[#F4F4F5] bg-white md:hidden transition-[transform,opacity] duration-300 ease-out ${
        mobileHideBottomNav
          ? 'translate-y-full opacity-0 pointer-events-none'
          : 'translate-y-0 opacity-100'
      }`}
      style={{
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        // Al mantener apretado, iOS ya no ofrece el menú de selección
        // (Copy/Look Up) sobre los íconos. El feedback de "pressed" lo da el
        // `active:opacity-60` de cada lugar. Mismo tratamiento que la barra vieja.
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        userSelect: 'none',
      }}
    >
      {/* 64px de contenido + el safe area de abajo. En el diseño la barra mide 86,
          pero esos 22px de más son la franja del indicador de home de iOS, que acá
          la pone `env(safe-area-inset-bottom)` y en un Android sin barra de gestos
          no debería existir. */}
      <div className="flex h-16 items-stretch">
        <TabNav tab={TAB_INICIO} apagada={herramientasAbierta} onSalir={onDejarHerramientas} />
        {uiNueva ? (
          <TabNav tab={TABS_DERECHA[0]} apagada={herramientasAbierta} onSalir={onDejarHerramientas} />
        ) : (
          segundoLugar
        )}

        {/* Botón central. Es un `button` y no un `NavLink` a propósito: abre el
            sheet sobre la pantalla en la que estés, sin cambiar la URL, así el
            back del sistema no queda cargado con un paso intermedio.

            El círculo va posicionado y no en el flujo de la columna: tiene que
            SALIR por arriba del borde de la barra (27px, la proporción del
            diseño) mientras su etiqueta se mantiene en la misma línea de base que
            las otras cuatro. Con márgenes negativos, cualquier cambio de alto de
            la barra desalineaba una de las dos cosas. */}
        <button
          type="button"
          onClick={onGenerar}
          aria-label={uiNueva ? 'Crear' : 'Generar'}
          className="relative flex-1 active:opacity-60"
        >
          {/* 56px y no 64 — ADR-2026-08-04-el-boton-de-generar-se-afina. El `bottom`
              sube de 27 a 30px para que entre el círculo y su etiqueta queden 8px: la
              etiqueta tiene `leading-none`, así que su borde superior está a 22px del
              piso de la barra (11 de `bottom` + 11 de alto). El círculo asoma 22px por
              arriba del borde de la barra.

              FONDO: el degradé azul del botón "Generar imagen" del onboarding —
              ADR-2026-08-20-el-generar-de-la-barra-usa-el-azul-del-onboarding. La clase
              va copiada CARÁCTER POR CARÁCTER de `AccountSetupProduct.tsx`, y no es
              cosmética la insistencia: Tailwind genera las clases arbitrarias escaneando
              el texto de los fuentes, así que una variante equivalente pero escrita
              distinto (otro orden, otros decimales) sería una regla NUEVA — y en este
              repo hay clases arbitrarias que no llegan al CSS. Verificado contra
              `dist/assets/*.css`: el degradé ya está ahí.

              Acá había además `bg-[#D6F8FB]` (celeste claro) y un aro `ring-1
              ring-[#1644EB]/25`. El aro se va con el celeste: existía para despegar un
              círculo claro del blanco de la barra, y sobre el azul saturado es azul al
              25% sobre azul — invisible. El botón del onboarding tampoco lo tiene. */}
          <span className="absolute bottom-[30px] left-1/2 grid h-14 w-14 -translate-x-1/2 place-items-center rounded-full bg-[linear-gradient(181.83deg,#2563F5_8.76%,#0436AE_90.56%)]">
            {/* `auto_awesome` es el mismo glifo de chispa del diseño, y el que ya
                usa el modo automático en la sidebar. Sin clase de variación: los
                ajustes de la familia (`FILL 1, wght 400`) son los de la sidebar.

                BLANCO y ya no `text-brand-primary`: el azul de marca sobre el degradé
                azul no se veía. Es el mismo `#FAFAFA` que usa el botón del onboarding
                para su ícono y su texto, no un blanco puro. */}
            <span className="material-symbols-rounded text-[28px] leading-none text-[#FAFAFA]">
              auto_awesome
            </span>
          </span>
          <span className="absolute bottom-[11px] left-1/2 -translate-x-1/2 text-[11px] font-semibold leading-none text-black/60">
            {uiNueva ? 'Crear' : 'Generar'}
          </span>
        </button>

        {uiNueva ? (
          <>
            <TabAccion
              label="Herramientas"
              icono="handyman"
              activa={herramientasAbierta}
              onClick={() => onHerramientas?.()}
            />
            <TabNav tab={TABS_DERECHA[1]} apagada={herramientasAbierta} onSalir={onDejarHerramientas} />
          </>
        ) : (
          TABS_DERECHA.map((t) => <TabNav key={t.to} tab={t} />)
        )}
      </div>
    </nav>
  );
}
